import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import HistoryIcon from "@mui/icons-material/History";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { DocumentView } from "../components/FinalBlueprintOutputs";
import {
  downloadCorrectionBlueprintPdf,
  getBuildCheck,
  getCorrectionBlueprint,
  getProjectBlueprints,
  listBuildChecks,
  recheckBuild,
  runBuildCheck,
} from "../api/apiClient";

/**
 * Second Pair of Eyes — Check My Build, Correction Blueprints, Recheck.
 *
 * Lives inside the user's project dashboard: one button, one file, results.
 * The customer picks the OnePass Build Snapshot their coding tool generated;
 * OnePass validates it, matches it to the exact approved blueprint version it
 * names, compares, classifies and saves the run — with no separate Upload,
 * Submit or Compare step. Every Possible Issue arrives with its Correction
 * Blueprint already generated; "View Correction Blueprint" only ever opens
 * one. Recheck Build runs an updated Snapshot against the same check, and
 * every run is kept so the history shows progress after corrections.
 */

const CLASS_ORDER = {
  "Possible Issue": 0,
  "Blueprint Clarification Required": 1,
  "Cannot Verify": 2,
  Matched: 3,
};
const CONFIDENCE_ORDER = { High: 0, Medium: 1, "Needs Review": 2 };

const CLASS_CHIP = {
  Matched: { color: "success" },
  "Possible Issue": { color: "warning" },
  "Cannot Verify": { color: "default" },
  "Blueprint Clarification Required": { color: "info" },
};

const STATUS_BANNER = {
  all_matched: { severity: "success", text: "Every requirement in this Blueprint version is evidenced in the completed build." },
  possible_issues: { severity: "warning", text: "Some areas of the completed build may not match the approved Blueprint. Start with the findings below." },
  needs_evidence: { severity: "info", text: "No mismatches were found, but some requirements could not be verified from this Snapshot." },
};

const OUTCOME_CHIP = {
  resolved: { label: "Resolved", color: "success" },
  still_open: { label: "Still open", color: "warning" },
  changed: { label: "Changed — new evidence", color: "info" },
  unverified: { label: "Cannot verify", color: "default" },
  clarification_required: { label: "Blueprint clarification required", color: "info" },
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

/** Findings the developer should look at first: issues, then clarifications, then unverified. */
export function sortFindings(findings = []) {
  return [...findings].sort(
    (a, b) =>
      (CLASS_ORDER[a.classification] ?? 9) - (CLASS_ORDER[b.classification] ?? 9) ||
      (CONFIDENCE_ORDER[a.confidence] ?? 9) - (CONFIDENCE_ORDER[b.confidence] ?? 9),
  );
}

/**
 * Plain-text report for "Copy Findings for My Developer": easy to paste into
 * Codex, Claude Code, Cursor or a ticket. Says what we know, what we believe
 * and where to look — never a code location the Snapshot did not name.
 */
export function buildFindingsReport(check, projectTitle = "") {
  if (!check) return "";
  const { checked_against: against = {}, summary = {}, findings = [], requirements = [] } = check;
  const lines = [
    "OnePass Second Pair of Eyes — Findings for my developer",
    `Project: ${projectTitle || against.title || against.project_id || ""}`.trim(),
    `Blueprint Version ${against.blueprint_version} · Build Check #${check.check_number} · ${formatDate(check.created_at)}`,
    `Result: ${summary.matched ?? 0} Matched · ${summary.possible_issues ?? 0} Possible Issues · ${summary.cannot_verify ?? 0} Cannot Verify · ${summary.clarification_required ?? 0} Blueprint Clarification Required`,
    "",
  ];

  const sorted = sortFindings(findings);
  if (sorted.length === 0) {
    lines.push("No findings — every requirement in this Blueprint version is evidenced.");
  }
  sorted.forEach((f, i) => {
    const ids = [f.primary_requirement_id, ...(f.related_requirement_ids || [])].filter(Boolean).join(", ");
    // Titles already lead with the classification ("Possible Issue — …").
    const heading = f.title?.startsWith(f.classification) ? f.title : `${f.classification} — ${f.title}`;
    lines.push(`${i + 1}. ${heading}${f.confidence ? ` (Confidence: ${f.confidence})` : ""}`);
    lines.push(`   Requirement IDs: ${ids}`);
    if (f.expected_behavior) lines.push(`   Expected behaviour: ${f.expected_behavior}`);
    if (f.what_we_observed) lines.push(`   What we observed: ${f.what_we_observed}`);
    if (f.what_we_believe) lines.push(`   What we believe: ${f.what_we_believe}`);
    if (f.why_we_think_that) lines.push(`   Why we think that: ${f.why_we_think_that}`);
    if (f.where_to_look) lines.push(`   Where to look: ${f.where_to_look}`);
    if (f.recommended_action) lines.push(`   Recommended action: ${f.recommended_action}`);
    if (f.correction_blueprint_id) {
      lines.push(`   Correction Blueprint: v${f.correction_blueprint_version} (finding ${f.finding_id} v${f.finding_version})`);
    }
    lines.push("");
  });

  const unverified = requirements.filter((r) => r.classification === "Cannot Verify");
  if (unverified.length) {
    lines.push("Could not be verified from the Snapshot (please include evidence next time):");
    unverified.forEach((r) => lines.push(`   ${r.requirement_id} — ${r.requirement}`));
    lines.push("");
  }
  lines.push("Generated by OnePass Second Pair of Eyes. The coding tool or developer makes the actual code change.");
  return lines.join("\n");
}

/** A blueprint document (sections of paragraph / list / table blocks) as plain text. */
export function documentToText(doc) {
  if (!doc) return "";
  const lines = [doc.meta?.title || "OnePass Correction Blueprint"];
  const c = doc.correction;
  if (c) {
    lines.push(
      `Project ${c.project_id} · Blueprint ${c.blueprint_id} · Version ${c.blueprint_version} · ` +
        `Finding ${c.finding_id} v${c.finding_version} · Correction Blueprint v${c.correction_version}` +
        (c.provisional ? " · PROVISIONAL — NEEDS REVIEW" : ""),
    );
  }
  lines.push("");
  (doc.sections || []).forEach((section, i) => {
    lines.push(`${i + 1}. ${section.title}`);
    (section.blocks || []).forEach((block) => {
      if (block.type === "paragraph") lines.push(`   ${block.text}`);
      if (block.type === "list") {
        if (block.title) lines.push(`   ${block.title}:`);
        (block.items || []).forEach((item) => lines.push(`   - ${item}`));
      }
      if (block.type === "table") {
        lines.push(`   ${(block.headers || []).join(" | ")}`);
        (block.rows || []).forEach((row) => lines.push(`   ${row.join(" | ")}`));
      }
    });
    lines.push("");
  });
  return lines.join("\n");
}

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement("textarea");
  area.value = text;
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  area.remove();
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read the file."));
    reader.readAsText(file);
  });
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const NOT_A_SNAPSHOT = {
  message: "We could not read this file as a OnePass Build Snapshot. Please regenerate the OnePass Build Snapshot from your coding environment.",
  issues: [],
};

function StatTile({ label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1, mt: 0.25, color }}>
        {value}
      </Typography>
    </Paper>
  );
}

function Detail({ label, value, mono = false, highlight = false }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: mono ? "ui-monospace, monospace" : undefined, fontWeight: highlight ? 600 : 400 }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function FindingCard({ finding, onViewCorrection }) {
  const ids = [finding.primary_requirement_id, ...(finding.related_requirement_ids || [])].filter(Boolean);
  const chip = CLASS_CHIP[finding.classification] || { color: "default" };
  const hasCorrection = Boolean(finding.correction_blueprint_id);
  return (
    <Accordion disableGutters variant="outlined" sx={{ borderRadius: "8px !important", "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ pr: 1 }}>
          <Chip size="small" label={finding.classification} color={chip.color} sx={{ fontWeight: 700 }} />
          {finding.confidence && <Chip size="small" variant="outlined" label={`Confidence: ${finding.confidence}`} />}
          {finding.finding_type !== "requirement" && (
            <Chip size="small" variant="outlined" color="secondary" label={finding.finding_type === "system" ? "Whole build" : "Connected requirements"} />
          )}
          {hasCorrection && (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              icon={<FactCheckOutlinedIcon />}
              label={`Correction Blueprint v${finding.correction_blueprint_version}${finding.correction_provisional ? " · Provisional" : ""}`}
            />
          )}
          <Typography variant="body2" fontWeight={600}>{finding.title}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.25}>
          <Detail label="Requirement" value={`${ids.join(", ")} — ${finding.blueprint_requirement || ""}`} mono />
          <Detail label="What we observed" value={finding.what_we_observed} />
          <Detail label="What we believe" value={finding.what_we_believe} />
          <Detail label="Why we think that" value={finding.why_we_think_that} />
          <Detail label="Where to look" value={finding.where_to_look} highlight />
          <Detail label="Expected behaviour" value={finding.expected_behavior} />
          {finding.recommended_action && <Detail label="Recommended action" value={finding.recommended_action} />}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
            <Tooltip title={hasCorrection ? "Open the Correction Blueprint generated with this check" : "No Correction Blueprint has been generated for this finding yet."}>
              <span>
                <Button
                  size="small"
                  variant={hasCorrection ? "contained" : "outlined"}
                  startIcon={<FactCheckOutlinedIcon />}
                  disabled={!hasCorrection}
                  onClick={() => hasCorrection && onViewCorrection(finding.correction_blueprint_id)}
                >
                  View Correction Blueprint
                </Button>
              </span>
            </Tooltip>
            {hasCorrection && finding.correction_reused && (
              <Typography variant="caption" color="text.secondary">
                Same finding, same evidence — reusing the Correction Blueprint from Build Check #{finding.correction_first_check_number}.
              </Typography>
            )}
            {hasCorrection && !finding.correction_reused && finding.correction_blueprint_version > 1 && (
              <Typography variant="caption" color="text.secondary">
                New evidence — Correction Blueprint updated to v{finding.correction_blueprint_version}; earlier versions are kept.
              </Typography>
            )}
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function RecheckPanel({ recheck }) {
  const summary = recheck.summary || {};
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mt: 2, borderColor: "primary.light" }} data-testid="recheck-panel">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <ReplayIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>
          Recheck of Build Check #{recheck.of_check_number}
        </Typography>
        {Object.entries(summary).filter(([, n]) => n > 0).map(([key, n]) => (
          <Chip key={key} size="small" color={OUTCOME_CHIP[key]?.color || "default"} label={`${n} ${OUTCOME_CHIP[key]?.label?.toLowerCase() || key}`} />
        ))}
      </Stack>
      <TableContainer sx={{ mt: 1 }}>
        <Table size="small" aria-label="recheck outcomes">
          <TableHead>
            <TableRow>
              <TableCell><strong>Finding</strong></TableCell>
              <TableCell><strong>Before</strong></TableCell>
              <TableCell><strong>Now</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(recheck.outcomes || []).map((o) => (
              <TableRow key={o.finding_id}>
                <TableCell>{o.title}</TableCell>
                <TableCell><Chip size="small" label={o.classification} color={(CLASS_CHIP[o.classification] || {}).color || "default"} /></TableCell>
                <TableCell>
                  <Chip size="small" label={OUTCOME_CHIP[o.outcome]?.label || o.outcome} color={OUTCOME_CHIP[o.outcome]?.color || "default"} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function SecondPairOfEyesPage() {
  const { sessionId } = useParams();
  const fileInputRef = useRef(null);
  const recheckInputRef = useRef(null);

  const [project, setProject] = useState(null);
  const [blueprints, setBlueprints] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [rejection, setRejection] = useState(null);
  const [notice, setNotice] = useState(null);
  const [current, setCurrent] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [correction, setCorrection] = useState(null);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionLoading, setCorrectionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bpData, historyData] = await Promise.all([
        getProjectBlueprints(sessionId),
        listBuildChecks({ projectId: sessionId }),
      ]);
      setProject({ id: bpData.project_id, title: bpData.project_title });
      setBlueprints(bpData.blueprints || []);
      setHistory(historyData.checks || []);
    } catch (err) {
      console.error("Failed to load Second Pair of Eyes", err);
      setError(err.response?.status === 404 ? "This project was not found." : "Could not load this project. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshHistory = async () => {
    const historyData = await listBuildChecks({ projectId: sessionId });
    setHistory(historyData.checks || []);
  };

  const showRejection = (err) => {
    const detail = err.response?.data?.detail;
    if (detail && typeof detail === "object") {
      setRejection({ message: detail.message, issues: detail.issues || [] });
    } else {
      setRejection({ message: detail || "We could not check this build. Please try again.", issues: [] });
    }
  };

  /** Select Snapshot → everything else happens automatically. */
  const runFromFile = async (event, run) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setRejection(null);
    setNotice(null);
    setChecking(true);
    try {
      let snapshot;
      try {
        snapshot = JSON.parse(await readFileAsText(file));
      } catch {
        setRejection(NOT_A_SNAPSHOT);
        return;
      }
      const result = await run(snapshot);
      setCurrent(result);
      setShowAll(false);
      setNotice(
        result.recheck
          ? `Recheck saved as Build Check #${result.check_number} — ${result.recheck.summary?.resolved ?? 0} finding(s) resolved.`
          : `Build Check #${result.check_number} saved.`,
      );
      await refreshHistory();
    } catch (err) {
      showRejection(err);
    } finally {
      setChecking(false);
    }
  };

  const handleFileChosen = (event) => runFromFile(event, (snapshot) => runBuildCheck(snapshot));
  const handleRecheckChosen = (event) => runFromFile(event, (snapshot) => recheckBuild(current.id, snapshot));

  const openCheck = async (checkId) => {
    setError(null);
    try {
      setCurrent(await getBuildCheck(checkId));
      setShowAll(false);
    } catch (err) {
      console.error("Could not load build check", err);
      setError("Could not load that build check.");
    }
  };

  const handleCopy = async () => {
    try {
      await copyText(buildFindingsReport(current, project?.title));
      setNotice("Findings copied — paste them to your developer or coding tool.");
    } catch (err) {
      console.error("Copy failed", err);
      setError("Could not copy to the clipboard.");
    }
  };

  /** Opens the Correction Blueprint generated with the check — never generates one. */
  const openCorrection = async (correctionId) => {
    setCorrectionOpen(true);
    setCorrectionLoading(true);
    setCorrection(null);
    try {
      setCorrection(await getCorrectionBlueprint(correctionId));
    } catch (err) {
      console.error("Could not load correction blueprint", err);
      setError("Could not load that Correction Blueprint.");
      setCorrectionOpen(false);
    } finally {
      setCorrectionLoading(false);
    }
  };

  const copyCorrection = async () => {
    try {
      await copyText(documentToText(correction?.document));
      setNotice("Correction Blueprint copied — hand it to your developer or coding tool.");
    } catch (err) {
      console.error("Copy failed", err);
      setError("Could not copy to the clipboard.");
    }
  };

  const downloadCorrection = async () => {
    try {
      const blob = await downloadCorrectionBlueprintPdf(correction.id);
      triggerDownload(blob, `correction-${correction.finding_id}-v${correction.correction_version}.pdf`);
    } catch (err) {
      console.error("PDF download failed", err);
      setError("Could not download the Correction Blueprint PDF.");
    }
  };

  const findings = useMemo(() => sortFindings(current?.findings || []), [current]);
  const requirementFindings = findings.filter((f) => f.finding_type === "requirement");
  const gapFindings = findings.filter((f) => f.finding_type !== "requirement");

  // Group history per approved blueprint (a project can hold more than one
  // approved blueprint at "version 1"), newest approval first.
  const historyByBlueprint = useMemo(() => {
    const order = blueprints.map((b) => b.blueprint_id);
    const groups = {};
    history.forEach((c) => {
      (groups[c.blueprint_id] ||= []).push(c);
    });
    return Object.entries(groups).sort((a, b) => {
      const ia = order.indexOf(a[0]);
      const ib = order.indexOf(b[0]);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  }, [history, blueprints]);

  const blueprintLabel = (blueprintId, fallbackVersion) => {
    const bp = blueprints.find((b) => b.blueprint_id === blueprintId);
    if (!bp) return `Blueprint Version ${fallbackVersion}`;
    return `Blueprint Version ${bp.blueprint_version} · approved ${formatDate(bp.approved_at)}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const banner = current ? STATUS_BANNER[current.status] : null;
  const summary = current?.summary || {};

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 0.5, sm: 1 }, py: 2 }}>
      <Button component={Link} to="/build/dashboard" startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 1 }}>
        My Projects
      </Button>

      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="primary" fontWeight={700}>Second Pair of Eyes</Typography>
          <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            {project?.title || "Check My Build"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
            Compare your completed build against this approved Blueprint and identify areas your
            developer may want to review. Select the OnePass Build Snapshot your coding tool generated —
            the check runs automatically.
          </Typography>
        </Box>
        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            hidden
            data-testid="snapshot-file-input"
            onChange={handleFileChosen}
          />
          <input
            ref={recheckInputRef}
            type="file"
            accept="application/json,.json"
            hidden
            data-testid="recheck-file-input"
            onChange={handleRecheckChosen}
          />
          <Button
            variant="contained"
            size="large"
            startIcon={checking ? <CircularProgress size={18} color="inherit" /> : <VisibilityOutlinedIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={checking || blueprints.length === 0}
          >
            {checking ? "Checking your build…" : current ? "Check My Build again" : "Check My Build"}
          </Button>
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {blueprints.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          This project has no approved Blueprint yet. Approve a Blueprint in the build flow first — the
          Build Snapshot instructions are included in its Developer Package.
        </Alert>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {blueprints.map((bp) => (
            <Chip
              key={bp.blueprint_id}
              variant="outlined"
              label={`Blueprint Version ${bp.blueprint_version} · ${bp.requirement_count} requirements · approved ${formatDate(bp.approved_at)}`}
            />
          ))}
        </Stack>
      )}

      {rejection && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRejection(null)}>
          <Typography variant="body2" fontWeight={700}>{rejection.message}</Typography>
          {rejection.issues?.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {rejection.issues.slice(0, 6).map((issue, i) => (
                <li key={`${issue.rule_id}-${i}`}>
                  <Typography variant="body2">
                    <strong>{issue.rule_id}</strong> — {issue.message}
                  </Typography>
                </li>
              ))}
            </Box>
          )}
        </Alert>
      )}

      {current && (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 3 }} data-testid="results">
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
            <Box>
              <Typography variant="h6" fontWeight={700}>Second Pair of Eyes Results</Typography>
              <Typography variant="body2" color="text.secondary">
                This build was checked against <strong>Blueprint Version {current.checked_against?.blueprint_version}</strong>
                {" · "}Build Check #{current.check_number}
                {current.recheck ? ` (recheck of #${current.recheck.of_check_number})` : ""} · {formatDate(current.created_at)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
                Copy Findings for My Developer
              </Button>
              <Tooltip describeChild title="After your developer corrects the build and the coding tool generates an updated Snapshot, check it against this same Blueprint version and these findings.">
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ReplayIcon />}
                  disabled={checking}
                  onClick={() => recheckInputRef.current?.click()}
                >
                  Recheck Build
                </Button>
              </Tooltip>
            </Stack>
          </Stack>

          <Grid container spacing={1.5} sx={{ mt: 2 }}>
            <Grid item xs={6} sm={4} md={2.4}><StatTile label="Requirements Checked" value={summary.requirements_checked ?? 0} /></Grid>
            <Grid item xs={6} sm={4} md={2.4}><StatTile label="Matched" value={summary.matched ?? 0} color="success.main" /></Grid>
            <Grid item xs={6} sm={4} md={2.4}><StatTile label="Possible Issues" value={summary.possible_issues ?? 0} color={(summary.possible_issues ?? 0) > 0 ? "warning.main" : undefined} /></Grid>
            <Grid item xs={6} sm={6} md={2.4}><StatTile label="Cannot Verify" value={summary.cannot_verify ?? 0} /></Grid>
            <Grid item xs={6} sm={6} md={2.4}><StatTile label="Clarification Required" value={summary.clarification_required ?? 0} color={(summary.clarification_required ?? 0) > 0 ? "info.main" : undefined} /></Grid>
          </Grid>

          {banner && <Alert severity={banner.severity} sx={{ mt: 2 }}>{banner.text}</Alert>}

          {current.recheck && <RecheckPanel recheck={current.recheck} />}

          {requirementFindings.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.25 }}>
                Areas We Think Your Developer Should Review
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Each Possible Issue comes with its Correction Blueprint, generated with this check.
              </Typography>
              <Stack spacing={1}>
                {requirementFindings.map((f) => <FindingCard key={f.finding_id} finding={f} onViewCorrection={openCorrection} />)}
              </Stack>
            </Box>
          )}

          {gapFindings.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.25 }}>
                Completed Build Gap Findings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Gaps that only show when the finished pieces are looked at together.
              </Typography>
              <Stack spacing={1}>
                {gapFindings.map((f) => <FindingCard key={f.finding_id} finding={f} onViewCorrection={openCorrection} />)}
              </Stack>
            </Box>
          )}

          <Divider sx={{ my: 2.5 }} />
          <Button size="small" onClick={() => setShowAll((v) => !v)} endIcon={<ExpandMoreIcon sx={{ transform: showAll ? "rotate(180deg)" : "none" }} />}>
            {showAll ? "Hide" : "Show"} all {summary.requirements_checked ?? 0} requirements
          </Button>
          <Collapse in={showAll}>
            <TableContainer sx={{ mt: 1, overflowX: "auto" }}>
              <Table size="small" aria-label="all requirements">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Requirement ID</strong></TableCell>
                    <TableCell><strong>Requirement</strong></TableCell>
                    <TableCell><strong>Result</strong></TableCell>
                    <TableCell><strong>Confidence</strong></TableCell>
                    <TableCell><strong>Snapshot said</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(current.requirements || []).map((r) => (
                    <TableRow key={r.requirement_id} hover>
                      <TableCell sx={{ fontFamily: "ui-monospace, monospace", whiteSpace: "nowrap" }}>{r.requirement_id}</TableCell>
                      <TableCell>{r.requirement}</TableCell>
                      <TableCell><Chip size="small" label={r.classification} color={(CLASS_CHIP[r.classification] || {}).color || "default"} /></TableCell>
                      <TableCell>{r.confidence || "—"}</TableCell>
                      <TableCell>{r.evidence_status || "not reported"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryIcon color="action" />
          <Box>
            <Typography variant="h6" fontWeight={700}>Build Check History</Typography>
            <Typography variant="body2" color="text.secondary">
              Every check is kept under the Blueprint version it was run against, so you can see progress after corrections.
              Issue → Correction Blueprint → Correction → Recheck → Result.
            </Typography>
          </Box>
        </Box>
        <Divider />
        {history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
            No build checks yet. Select a Build Snapshot above to run the first one.
          </Typography>
        ) : (
          historyByBlueprint.map(([blueprintId, checks]) => (
            <Box key={blueprintId}>
              <Typography variant="overline" sx={{ px: 2, pt: 1.5, display: "block" }} color="text.secondary">
                {blueprintLabel(blueprintId, checks[0]?.blueprint_version)}
              </Typography>
              <TableContainer>
                <Table size="small" aria-label={`build checks for ${blueprintLabel(blueprintId, checks[0]?.blueprint_version)}`}>
                  <TableBody>
                    {checks.map((c) => (
                      <TableRow
                        key={c.id}
                        hover
                        selected={current?.id === c.id}
                        onClick={() => openCheck(c.id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <strong>Build Check #{c.check_number}</strong>
                          {c.recheck_of_check_number && (
                            <Chip size="small" variant="outlined" color="primary" icon={<ReplayIcon />} label={`Recheck of #${c.recheck_of_check_number}`} sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(c.created_at)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={c.status === "all_matched" ? "All matched" : c.status === "possible_issues" ? "Possible issues" : "Needs evidence"}
                            color={c.status === "all_matched" ? "success" : c.status === "possible_issues" ? "warning" : "default"}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "text.secondary" }}>
                          {c.summary?.matched ?? 0} matched · {c.summary?.possible_issues ?? 0} possible issues · {c.summary?.cannot_verify ?? 0} cannot verify
                          {c.correction_count ? ` · ${c.correction_count} correction blueprint${c.correction_count === 1 ? "" : "s"}` : ""}
                        </TableCell>
                        <TableCell align="right"><Button size="small">View</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))
        )}
      </Paper>

      <Dialog open={correctionOpen} onClose={() => setCorrectionOpen(false)} fullWidth maxWidth="md" scroll="paper">
        <DialogTitle sx={{ pr: 6 }}>
          {correction?.document?.meta?.title || "Correction Blueprint"}
        </DialogTitle>
        <DialogContent dividers>
          {correctionLoading || !correction ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={`Finding ${correction.finding_id} · v${correction.finding_version}`} sx={{ fontFamily: "ui-monospace, monospace" }} />
                <Chip size="small" color="primary" variant="outlined" label={`Correction Blueprint v${correction.correction_version}`} />
                {correction.confidence && <Chip size="small" variant="outlined" label={`Confidence: ${correction.confidence}`} />}
                {correction.provisional && <Chip size="small" color="warning" label="Provisional — Needs Review" />}
                <Chip
                  size="small"
                  color={correction.resolution === "resolved" ? "success" : correction.resolution === "open" ? "warning" : "default"}
                  label={correction.resolution === "resolved" ? "Resolved by a later check" : correction.resolution === "open" ? "Open" : "Cannot verify yet"}
                />
                <Chip size="small" variant="outlined" label={`First found in Build Check #${correction.first_check_number}`} />
              </Stack>
              {correction.versions?.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  Versions kept: {correction.versions.map((v) => `v${v.correction_version} (Build Check #${v.first_check_number})`).join(" · ")}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Generated automatically with Build Check #{correction.first_check_number} from the approved Blueprint
                Version {correction.blueprint?.blueprint_version}. Hand it to your developer or coding tool — they make the change; OnePass does not touch your code.
              </Typography>
              <Divider />
              <DocumentView document={correction.document} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyIcon />} onClick={copyCorrection} disabled={!correction}>Copy Blueprint</Button>
          <Button startIcon={<PictureAsPdfIcon />} onClick={downloadCorrection} disabled={!correction}>Download PDF</Button>
          <Button onClick={() => setCorrectionOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
